import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';
import {
  EmailAuthProvider,
  MultiFactorError,
  SAMLAuthProvider,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { auth } from '../../../api/firebase/config';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import { MFA_TOTP_CODE_LENGTH } from '../../../constants/enum';
import {
  gray40,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogText, dialogTitle } from '../../../constants/location';
import { resolveSamlReauthProviderId } from '../../../utils/samlUser';
import CustomTextfield from '../form/CustomTextfield';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 解除が完了したとき */
  onUnenrolled: () => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  bodyText: {
    ...dialogText,
    marginBottom: '16px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    marginBottom: '16px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  actionButton: {
    height: '44px',
    minWidth: '140px',
  },
};

/**
 * 認証アプリ（TOTP）解除ダイアログ（#2984）。
 * なりすまし解除を防ぐため、パスワード + 認証アプリの確認コードで再認証してから解除する。
 * SAML SSO 専用ユーザーはパスワードを持たないため、IdP のポップアップで再認証する（#3214）。
 * テナント強制が有効な場合、解除しても次回ログイン時に再設定が求められる。
 */
const MfaUnenrollDialog: React.FC<Props> = ({ open, onClose, onUnenrolled }) => {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // SAML 再認証を使うユーザーか（パスワードを持たない SAML 専用ユーザーのみ・#3214）
  const samlProviderId = resolveSamlReauthProviderId(auth.currentUser);

  useEffect(() => {
    if (open) {
      setPassword('');
      setCode('');
      setErrorMessage('');
    }
  }, [open]);

  const handleUnenroll = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    const email = user.email ?? '';
    // パスワード再認証はメールアドレスが必須（SAML 再認証では使わない）
    if (!samlProviderId && !email) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      // 再認証（MFA 有効ユーザーは第二要素の解決が必要になる）。
      // SAML 専用ユーザーは IdP のポップアップで再認証する。ポップアップは
      // クリック直後に開く必要があるため、ここまでに await を挟まない（#3214）
      try {
        if (samlProviderId) {
          await reauthenticateWithPopup(user, new SAMLAuthProvider(samlProviderId));
        } else {
          await reauthenticateWithCredential(user, EmailAuthProvider.credential(email, password));
        }
      } catch (e) {
        if (!(e instanceof FirebaseError) || e.code !== 'auth/multi-factor-auth-required') {
          throw e;
        }
        const resolver = getMultiFactorResolver(auth, e as MultiFactorError);
        const totpHint = resolver.hints.find(
          hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
        );
        if (!totpHint) throw e;
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
        await resolver.resolveSignIn(assertion);
      }

      const factors = multiFactor(user).enrolledFactors.filter(
        factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      for (const factor of factors) {
        // eslint-disable-next-line no-await-in-loop
        await multiFactor(user).unenroll(factor);
      }
      onUnenrolled();
      onClose();
    } catch (e) {
      if (e instanceof FirebaseError) {
        setErrorMessage(
          e.code === 'auth/invalid-verification-code'
            ? '確認コードが正しくありません。認証アプリに表示されている最新のコードを入力してください。'
            : getErrorMessageFromAuthErrorCode(e.code),
        );
      } else {
        Sentry.captureException(e);
        setErrorMessage('認証アプリの解除に失敗しました。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>認証アプリを解除しますか？</Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}

      <Typography sx={styles.bodyText}>
        {samlProviderId
          ? '解除すると、次回のログインで確認コードの入力が不要になります。確認のため、認証アプリの確認コードを入力し、「解除する」を押したあとに開く ID プロバイダの画面で再認証してください。'
          : '解除すると、次回からパスワードのみでログインできるようになります。確認のため、パスワードと認証アプリの確認コードを入力してください。'}
      </Typography>
      {/* SAML 専用ユーザーはパスワードを持たないため、パスワード欄は出さない（#3214） */}
      {!samlProviderId && (
        <CustomTextfield
          type="password"
          labelText="パスワード"
          inputWidth="100%"
          fontSizeIs14px
          value={password}
          onChange={(e): void => setPassword(e.target.value)}
          formMb="16px"
        />
      )}
      <CustomTextfield
        labelText={`確認コード（${MFA_TOTP_CODE_LENGTH}桁）`}
        inputWidth="100%"
        fontSizeIs14px
        placeholder="123456"
        value={code}
        onChange={(e): void => setCode(e.target.value)}
        formMb="0"
      />
      <Box sx={styles.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleUnenroll}
          disabled={
            submitting ||
            (!samlProviderId && !password) ||
            code.trim().length !== MFA_TOTP_CODE_LENGTH
          }
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={[primaryButtonBackgroundColor, styles.actionButton]}
        >
          解除する
        </Button>
      </Box>
    </Dialog>
  );
};

export default MfaUnenrollDialog;
